import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TimelineNodeTooltipProps {
  id: string;
  anchor: HTMLElement;
  onDismiss: () => void;
  children: React.ReactNode;
}

// The graph scrolls horizontally. A portal keeps node details from being
// clipped by that scroll container, including at the final node.
export default function TimelineNodeTooltip({ id, anchor, onDismiss, children }: TimelineNodeTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!ref.current) return;
      const node = anchor.getBoundingClientRect();
      const tooltip = ref.current.getBoundingClientRect();
      const margin = 8;
      const preferredLeft = node.right + margin + tooltip.width <= window.innerWidth - margin
        ? node.right + margin
        : node.left - tooltip.width - margin;
      setPosition({
        left: Math.max(margin, Math.min(preferredLeft, window.innerWidth - tooltip.width - margin)),
        top: Math.max(margin, Math.min(node.top, window.innerHeight - tooltip.height - margin))
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchor, children]);

  useLayoutEffect(() => {
    const dismissBehindModal = () => {
      if (document.querySelector('.modal.d-block')) onDismiss();
    };
    dismissBehindModal();
    const observer = new MutationObserver(dismissBehindModal);
    observer.observe(document.body, { childList: true, subtree: true });
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [onDismiss]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      className="bg-white text-dark border rounded-3 shadow p-2"
      role="tooltip"
      style={{
        position: 'fixed',
        ...position,
        width: 264,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        overflow: 'auto',
        // Above the fullscreen graph overlay (--flow-graph-overlay-z, 1048) so
        // node details still show there; below modals, which dismiss it anyway.
        zIndex: 1049,
        pointerEvents: 'none'
      }}
    >
      {children}
    </div>,
    document.body
  );
}
